import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { keys } from "@/api/queryKeys";
import type { Me, Message, SendMessageResponse } from "@/api/types";

// Page size for the backwards "load older" fetch. Matches the
// backend default and the UI expectation that one IntersectionObserver
// Initial load keeps recent chat responsive; older history streams in
// on scroll. Matching the older-page limit prevents every session open
// from hydrating hundreds of off-screen rows.
const INITIAL_MESSAGE_LIMIT = 200;
// Older pages stay at 200 so one scroll-top hit yields a chunk
// roughly the size of a screen of dense chat.
const OLDER_PAGE_LIMIT = 200;

export type FetchOlderResult = {
  added: number;
  reachedStart: boolean;
};

export function useMessages(sessionId: string | undefined) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: keys.messages(sessionId ?? ""),
    queryFn: async () => {
      const cached = qc.getQueryData<Message[]>(keys.messages(sessionId ?? ""));
      const afterSeq = highestPersistedMessageSeq(cached);
      const url =
        afterSeq > 0
          ? `/sessions/${sessionId}/messages?after_seq=${afterSeq}`
          : `/sessions/${sessionId}/messages?limit=${INITIAL_MESSAGE_LIMIT}`;
      const next = (await api.get<Message[]>(url)).data;
      return afterSeq > 0 ? mergeMessagePages(cached, next) : next;
    },
    enabled: Boolean(sessionId),
    // A passive viewer (not attached to a live turn stream) only saw
    // new transcript rows — another participant's decision, an intent
    // change, a draft card — after a reload. The incremental
    // ``after_seq`` fetch is tiny, so poll it while the tab is visible.
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  // ``fetchOlder`` is the user-facing "load older messages" trigger.
  // Reads the lowest persisted seq from the cache, fetches the
  // ``OLDER_PAGE_LIMIT`` rows immediately preceding it, and merges
  // them into the cached array. ``reachedStart`` is the signal the
  // caller uses to stop the IntersectionObserver loop — true when the
  // backend returns fewer rows than we asked for (we've drained the
  // history) or when the oldest visible message is already seq=1.
  const fetchOlder = useCallback(async (): Promise<FetchOlderResult> => {
    if (!sessionId) return { added: 0, reachedStart: true };
    const cached = qc.getQueryData<Message[]>(keys.messages(sessionId));
    const oldestSeq = lowestPersistedMessageSeq(cached);
    // Cache empty (initial mount race) — let the standard ``useQuery``
    // load do its work and report nothing-fetched without faking a
    // "reached the start" signal.
    if (!Number.isFinite(oldestSeq)) {
      return { added: 0, reachedStart: false };
    }
    // ``oldestSeq <= 1`` used to short-circuit to ``reachedStart=true``
    // without fetching, on the assumption that "we already have seq=1
    // so there's nothing older". That broke when seq=1 *was* in cache
    // but the user's own first prompt was NOT — the SPA showed
    // "you've reached the beginning" with the original prompt missing
    // and refused to ask the server. Always defer to the backend; if
    // it returns nothing, that's the authoritative reachedStart.
    const url = `/sessions/${sessionId}/messages?before_seq=${oldestSeq}&limit=${OLDER_PAGE_LIMIT}`;
    const older = (await api.get<Message[]>(url)).data;
    if (older.length === 0) return { added: 0, reachedStart: true };
    const merged = mergeMessagePages(cached, older);
    qc.setQueryData(keys.messages(sessionId), merged);
    return {
      added: older.length,
      reachedStart: older.length < OLDER_PAGE_LIMIT,
    };
  }, [sessionId, qc]);

  return Object.assign(query, { fetchOlder });
}

function highestPersistedMessageSeq(messages: Message[] | undefined): number {
  return Math.max(
    0,
    ...(messages ?? [])
      .filter((message) => !message.id.startsWith("optimistic-"))
      .map((message) => message.seq),
  );
}

function lowestPersistedMessageSeq(messages: Message[] | undefined): number {
  const seqs = (messages ?? [])
    .filter((message) => !message.id.startsWith("optimistic-"))
    .map((message) => message.seq);
  if (seqs.length === 0) return Number.POSITIVE_INFINITY;
  return Math.min(...seqs);
}

function mergeMessagePages(
  cached: Message[] | undefined,
  next: Message[],
): Message[] {
  if (!cached?.length) return next;
  if (!next.length) return cached;

  const highestIncomingSeq = Math.max(...next.map((message) => message.seq));
  const byId = new Map<string, Message>();
  for (const message of cached) {
    if (
      message.id.startsWith("optimistic-") &&
      message.seq <= highestIncomingSeq
    ) {
      continue;
    }
    byId.set(message.id, message);
  }
  for (const message of next) {
    byId.set(message.id, message);
  }
  return [...byId.values()].sort((a, b) => {
    if (a.seq !== b.seq) return a.seq - b.seq;
    return a.created_at.localeCompare(b.created_at);
  });
}

/** How long a continuation send waits for the transcript refetch that
 *  brings in the decision it follows (see ``useSendMessage``). */
const CONTINUATION_TRANSCRIPT_WAIT_MS = 2_000;

export function useSendMessage(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input:
        | string
        | {
            content: string;
            reference_document_id?: string | null;
            /** Resuming a turn the user did not type — the continuation
             *  fired after a decision card is answered. */
            auto_continuation?: boolean;
          },
    ) => {
      const payload =
        typeof input === "string"
          ? { content: input }
          : {
              content: input.content,
              ...(input.reference_document_id
                ? { reference_document_id: input.reference_document_id }
                : {}),
              ...(input.auto_continuation ? { auto_continuation: true } : {}),
            };
      return (
        await api.post<SendMessageResponse>(
          `/sessions/${sessionId}/messages`,
          payload,
        )
      ).data;
    },
    onMutate: async (input) => {
      // A continuation's persisted row carries ``auto_continuation`` on its
      // part and renders as nothing (the decision row is the record); the
      // stand-in matches so the answer never flashes up as a chat bubble —
      // while it is sending, MessageList shows a quiet "Sending…" line.
      const autoContinuation =
        typeof input !== "string" && input.auto_continuation === true;
      // A continuation follows a decision the server has just recorded,
      // and the card asked for that record (a messages refetch) as it
      // closed. Cancelling the refetch below, as for any optimistic
      // insert, left the transcript without it for the whole admission.
      // Take it in first: the stand-in then comes after it, and a
      // failure restores a transcript that still has it. A slow GET does
      // not hold the continuation up for long: past the wait it is
      // cancelled below like any other.
      if (autoContinuation) {
        // Drop a load already on its way first. It may have been sent
        // before the decision was recorded, and while the query has no
        // data yet (the view's first load), a refetch joins it instead of
        // replacing it: the record would then only arrive with the next
        // poll.
        await qc.cancelQueries({ queryKey: keys.messages(sessionId) });
        let timer: ReturnType<typeof setTimeout> | undefined;
        await Promise.race([
          qc.refetchQueries({
            queryKey: keys.messages(sessionId),
            exact: true,
            type: "active",
          }),
          new Promise((resolve) => {
            timer = setTimeout(resolve, CONTINUATION_TRANSCRIPT_WAIT_MS);
          }),
        ]);
        clearTimeout(timer);
      }
      await qc.cancelQueries({ queryKey: keys.messages(sessionId) });
      const previous = qc.getQueryData<Message[]>(keys.messages(sessionId));
      const text = typeof input === "string" ? input : input.content;
      const optimistic: Message = {
        id: `optimistic-${Date.now()}`,
        seq: (previous?.at(-1)?.seq ?? 0) + 1,
        role: "user",
        parts: [
          {
            kind: "text",
            text,
            ...(autoContinuation ? { auto_continuation: true } : {}),
          },
        ],
        summary_text: text,
        token_usage: null,
        tool_call_id: null,
        parent_message_id: null,
        codex_event_id: null,
        // The viewer sent it: a shared session's author chip reads this,
        // and without it the stand-in said "Unknown" until the POST
        // answered and the persisted row replaced it.
        sender_user_id: qc.getQueryData<Me>(keys.me)?.user.id ?? null,
        created_at: new Date().toISOString(),
        // "Sending…" until the POST answers — it returns only after turn
        // admission, which can take seconds.
        sending: true,
      };
      qc.setQueryData<Message[]>(keys.messages(sessionId), [
        ...(previous ?? []),
        optimistic,
      ]);
      return { previous, optimisticId: optimistic.id };
    },
    onError: (
      _err,
      _vars,
      context: { previous?: Message[]; optimisticId?: string } | undefined,
    ) => {
      if (context?.previous !== undefined) {
        qc.setQueryData<Message[]>(keys.messages(sessionId), context.previous);
      }
    },
    onSuccess: (_data, _vars, context) => {
      // Delivered: drop the "Sending…" mark now rather than when the
      // refetch below swaps in the persisted row.
      qc.setQueryData<Message[]>(keys.messages(sessionId), (cached) =>
        cached?.map((message) =>
          message.id === context?.optimisticId
            ? { ...message, sending: false }
            : message,
        ),
      );
      return qc.invalidateQueries({ queryKey: keys.messages(sessionId) });
    },
  });
}

export type ToolResultBody = {
  message_id: string;
  tool_call_id: string | null;
  tool: string | null;
  output: string;
  exit_code: number | null;
  status: string | null;
};

export function useToolResult(
  sessionId: string | undefined,
  messageId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [
      "session",
      sessionId ?? "",
      "tool-result",
      messageId ?? "",
    ] as const,
    queryFn: async () =>
      (
        await api.get<ToolResultBody>(
          `/sessions/${sessionId}/messages/${messageId}/tool-result`,
        )
      ).data,
    enabled: Boolean(sessionId && messageId && enabled),
    staleTime: Infinity,
  });
}

/** Generate a Codex-summary of an existing session's conversation.
 *
 *  Used by the "new session from this" flow to seed the composer of
 *  the derivative session with a real summary (3–6 sentences
 *  covering what was done / pending / next) rather than a truncated
 *  copy of the last assistant message. Runs server-side via a
 *  short one-shot ``codex exec`` against a throwaway tmpdir; 60 s
 *  timeout on the backend, ~3–10 s typical. Mutation not query so
 *  the caller can fire it on demand (e.g. "regenerate summary"
 *  button).
 */
export function useSessionSummary() {
  return useMutation({
    mutationFn: async (sessionId: string) =>
      (await api.post<{ summary: string }>(`/sessions/${sessionId}/summary`))
        .data,
  });
}

export function useCancelTurn(sessionId: string) {
  return useMutation({
    mutationFn: async (turnId: string) => {
      await api.post(`/sessions/${sessionId}/turns/${turnId}/cancel`);
    },
  });
}

export function useApproveCall(sessionId: string) {
  return useMutation({
    mutationFn: async (payload: {
      call_id: string;
      decision: "approve" | "reject";
    }) =>
      (
        await api.post<{ ok: boolean }>(
          `/sessions/${sessionId}/approve`,
          payload,
        )
      ).data,
  });
}
